import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import './Header.css';

const Header = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user, logout, isAdmin, isManager, isVerified } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => document.body.classList.remove('mobile-menu-open');
  }, [mobileMenuOpen]);

  // Close mobile menu on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    if (mobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setShowDropdown(false);
    setMobileMenuOpen(false);
  };

  const getDashboardPath = () => {
    if (isAdmin()) return '/admin/dashboard';
    if (isManager()) return '/manager/dashboard';
    return '/admin';
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const isActivePath = (target) => {
    if (!target) return false;
    const path = target.split('?')[0];
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navClass = (target) =>
    `nav-link${isActivePath(target) ? ' nav-link--active' : ''}`;

  const ariaCurrent = (target) => (isActivePath(target) ? 'page' : undefined);

  const NavLinks = ({ onNavigate }) => (
    <>
      {isAuthenticated() ? (
        <>
          {isAdmin() && (
            <Link to="/admin/dashboard" className={navClass('/admin/dashboard')} aria-current={ariaCurrent('/admin/dashboard')} onClick={onNavigate}>
              <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="9" rx="1"/>
                <rect x="14" y="3" width="7" height="5" rx="1"/>
                <rect x="14" y="12" width="7" height="9" rx="1"/>
                <rect x="3" y="16" width="7" height="5" rx="1"/>
              </svg>
              {t('nav.dashboard')}
            </Link>
          )}
          {isManager() && isVerified() && (
            <Link to="/manager/dashboard" className={navClass('/manager/dashboard')} aria-current={ariaCurrent('/manager/dashboard')} onClick={onNavigate}>
              <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="9" rx="1"/>
                <rect x="14" y="3" width="7" height="5" rx="1"/>
                <rect x="14" y="12" width="7" height="9" rx="1"/>
                <rect x="3" y="16" width="7" height="5" rx="1"/>
              </svg>
              {t('nav.dashboard')}
            </Link>
          )}
          {isVerified() && (
            <Link to="/admin" className={navClass('/admin')} aria-current={ariaCurrent('/admin')} onClick={onNavigate}>
              <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 3v18M5 8h14M5 16h14"/>
              </svg>
              {t('nav.menu')}
            </Link>
          )}
          <Link to="/menu/9?mode=demo" className={navClass('/menu/9')} aria-current={ariaCurrent('/menu/9')} onClick={onNavigate}>
            <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polygon points="10 8 16 12 10 16 10 8"/>
            </svg>
            {t('nav.demo')}
          </Link>
        </>
      ) : (
        <>
          <a href="/#pricing" className="nav-link" onClick={onNavigate}>
            <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
            </svg>
            {t('nav.pricing')}
          </a>
          <Link to="/menu/9?mode=demo" className={navClass('/menu/9')} aria-current={ariaCurrent('/menu/9')} onClick={onNavigate}>
            <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polygon points="10 8 16 12 10 16 10 8"/>
            </svg>
            {t('nav.tryDemo')}
          </Link>
        </>
      )}
    </>
  );

  return (
    <header className="header">
      <div className="header-container">
        <Link to="/" className="logo" aria-label={t('brand.name')}>
          <div className="logo-icon">
            <img
              src="/mesoon-logo.svg"
              alt=""
              width="28"
              height="28"
              style={{ display: 'block' }}
            />
          </div>
          <span className="logo-text">{t('brand.name')}</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="nav-desktop">
          <NavLinks />
          
          {isAuthenticated() ? (
            <div className="user-menu" ref={dropdownRef}>
              <button 
                className="user-menu-trigger"
                onClick={() => setShowDropdown(!showDropdown)}
                aria-expanded={showDropdown}
                aria-haspopup="true"
              >
                <div className="user-avatar">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="user-info">
                  <span className="user-name">{user?.name}</span>
                  <span className="user-role">{user?.role}</span>
                </div>
                <svg className={`dropdown-chevron ${showDropdown ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              
              {showDropdown && (
                <div className="user-dropdown" role="menu">
                  <div className="dropdown-header">
                    <span className="dropdown-name">{user?.name}</span>
                    <span className="dropdown-email">{user?.email}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <Link 
                    to="/profile" 
                    className="dropdown-item"
                    onClick={() => setShowDropdown(false)}
                    role="menuitem"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    {t('nav.profile')}
                  </Link>
                  {isAdmin() && (
                    <Link 
                      to="/admin/users" 
                      className="dropdown-item"
                      onClick={() => setShowDropdown(false)}
                      role="menuitem"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 00-3-3.87"/>
                        <path d="M16 3.13a4 4 0 010 7.75"/>
                      </svg>
                      {t('nav.manageUsers')}
                    </Link>
                  )}
                  <div className="dropdown-divider"></div>
                  <button 
                    className="dropdown-item dropdown-item-danger"
                    onClick={handleLogout}
                    role="menuitem"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    {t('nav.signOut')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="auth-buttons">
              <Link to="/login" className="btn btn-ghost">{t('nav.signIn')}</Link>
              <Link to="/register" className="btn btn-primary">{t('nav.getStarted')}</Link>
            </div>
          )}
          <LanguageSwitcher />
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className={`mobile-menu-btn ${mobileMenuOpen ? 'open' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={t('nav.toggleMenu')}
          aria-expanded={mobileMenuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {/* Mobile menu overlay - tap outside to close */}
      {mobileMenuOpen && (
        <div
          className="nav-mobile-overlay visible"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Navigation */}
      <div className={`nav-mobile ${mobileMenuOpen ? 'open' : ''}`}>
        <nav className="nav-mobile-content" aria-label={t('nav.toggleMenu')}>
          <h2 className="nav-mobile-title">{t('nav.menu')}</h2>
          <NavLinks onNavigate={closeMobileMenu} />
          
          {isAuthenticated() ? (
            <>
              <div className="mobile-divider"></div>
              <div className="mobile-user-info">
                <div className="user-avatar large">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div>
                  <div className="mobile-user-name">{user?.name}</div>
                  <div className="mobile-user-role">{user?.role}</div>
                </div>
              </div>
              <Link to="/profile" className={navClass('/profile')} aria-current={ariaCurrent('/profile')} onClick={closeMobileMenu}>
                <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                {t('nav.profile')}
              </Link>
              {isAdmin() && (
                <Link to="/admin/users" className={navClass('/admin/users')} aria-current={ariaCurrent('/admin/users')} onClick={closeMobileMenu}>
                  <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 00-3-3.87"/>
                    <path d="M16 3.13a4 4 0 010 7.75"/>
                  </svg>
                  {t('nav.manageUsers')}
                </Link>
              )}
              <button onClick={handleLogout} className="nav-link nav-link-danger">
                <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                {t('nav.signOut')}
              </button>
              <div className="mobile-divider"></div>
              <div className="mobile-language-switcher">
                <LanguageSwitcher variant="buttons" />
              </div>
            </>
          ) : (
            <>
              <div className="mobile-divider"></div>
              <div className="mobile-auth-buttons">
                <Link to="/login" className="btn btn-secondary w-full" onClick={closeMobileMenu}>{t('nav.signIn')}</Link>
                <Link to="/register" className="btn btn-primary w-full" onClick={closeMobileMenu}>{t('nav.getStarted')}</Link>
              </div>
              <div className="mobile-divider"></div>
              <div className="mobile-language-switcher">
                <LanguageSwitcher variant="buttons" />
              </div>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
